import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkle, MagnifyingGlass } from '@phosphor-icons/react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GROUP_ORDER = ['DDR5', 'DDR4', 'DDR3', 'DDR2', 'Other'];
const CORE_LINKS = [
  ['/', 'Home'], ['/inventory', 'Inventory'], ['/news', 'Industry News'], ['/rfq', 'Request Quote'],
  ['/agents', 'Web3 Agent Commerce'], ['/for-agents', 'For AI Agents'], ['/order', 'Order Lookup'], ['/agent-activity', 'Payments Race'],
];

const groupOf = (desc = '') => (desc.toUpperCase().match(/DDR[2-5]/) || ['Other'])[0];

const ProductGroup = ({ title, items }) => (
  <section className="mb-10" data-testid={`sitemap-group-${title.toLowerCase()}`}>
    <h2 className="text-lg font-['JetBrains_Mono'] font-bold text-white mb-1">{title} Server Memory</h2>
    <p className="text-xs font-mono text-[#64748B] mb-4">{items.length} modules</p>
    <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5">
      {items.map((p) => (
        <li key={p.slug} className="text-sm truncate">
          <Link to={`/product/${p.slug}`} className="text-cyan-400 hover:text-white font-mono transition-colors" data-testid={`sitemap-link-${p.slug}`}>
            {p.name}
          </Link>
          <span className="text-[#94A3B8]"> — {p.description}</span>
          {p.enriched && <Sparkle size={11} weight="fill" className="inline ml-1 text-[#A855F7]" aria-label="AI specs" />}
        </li>
      ))}
    </ul>
  </section>
);

export default function SitemapPage() {
  const [data, setData] = useState({ items: [], total: 0, enriched: 0 });
  const [q, setQ] = useState('');
  const [cats, setCats] = useState([]);

  useEffect(() => {
    document.title = 'Site Map — All Server Memory Modules | ServerMemoryRFQ';
    axios.get(`${API_URL}/api/sitemap/products`).then((r) => setData(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/categories`).then((r) => setCats(r.data.items || [])).catch(() => {});
  }, []);

  const groups = useMemo(() => {
    const term = q.trim().toLowerCase();
    const out = {};
    data.items
      .filter((p) => !term || `${p.name} ${p.description}`.toLowerCase().includes(term))
      .forEach((p) => { const g = groupOf(p.description); out[g] = out[g] || []; out[g].push(p); });
    return out;
  }, [data.items, q]);

  return (
    <div className="min-h-screen" data-testid="sitemap-page">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-10">
        <h1 className="text-4xl sm:text-5xl font-['JetBrains_Mono'] font-bold text-cyan-400 mb-3">Site Map</h1>
        <p className="text-[#94A3B8] mb-2 max-w-3xl">
          Every enterprise server memory module in our live inventory, with technical specs and module architecture for each part.
        </p>
        <p className="text-xs font-mono text-[#64748B] mb-8" data-testid="sitemap-counts">
          {data.total.toLocaleString()} product pages · {data.enriched.toLocaleString()} with AI technical specs
        </p>

        <nav className="flex flex-wrap gap-x-5 gap-y-2 mb-10 pb-6 border-b border-[#1E293B]" data-testid="sitemap-core-links">
          {CORE_LINKS.map(([to, label]) => (
            <Link key={to} to={to} className="text-sm font-mono text-[#CBD5E1] hover:text-cyan-400 transition-colors">{label}</Link>
          ))}
        </nav>

        {cats.length > 0 && (
          <section className="mb-12" data-testid="sitemap-categories">
            <h2 className="text-lg font-['JetBrains_Mono'] font-bold text-white mb-4">Shop by Category</h2>
            <div className="flex flex-wrap gap-2">
              {cats.map((c) => (
                <Link key={c.slug} to={`/category/${c.slug}`} className="px-3 py-1.5 rounded border border-[#334155] hover:border-cyan-400 text-xs font-mono text-[#CBD5E1] transition-colors" data-testid={`sitemap-category-${c.slug}`}>
                  {c.title.replace(' Server Memory', '')} <span className="text-[#64748B]">({c.count})</span>
                </Link>
              ))}
            </div>
          </section>
        )}

        <div className="relative max-w-md mb-10">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by part number or spec…"
            className="pl-9 bg-[#05050A] border-[#334155] font-mono" data-testid="sitemap-filter-input" />
        </div>

        {GROUP_ORDER.filter((g) => groups[g]).map((g) => <ProductGroup key={g} title={g} items={groups[g]} />)}
      </main>
      <Footer />
    </div>
  );
}
